/**
 * ManagerReturnToWorkCard — appears on a department manager's dashboard
 * when one or more of their direct subordinates has punched in today
 * RIGHT AFTER a leave period. Each row exposes a "توثيق مباشرة العمل"
 * button that files an official Return-to-Work notification to admin.
 *
 * Backend endpoints:
 *   GET  /api/return-to-work/eligible → { count, items[], date }
 *   POST /api/return-to-work { employee_id, notes? } → creates a pending_admin request
 *
 * UX principles:
 *   • Hidden entirely when there is nothing to act on (count === 0) so
 *     managers with no returning subordinates today never see it.
 *   • One click = one confirm dialog = filed. No page navigation.
 *   • After a successful submission, the row disappears optimistically.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { CheckCircle, HandWaving, CalendarCheck, ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ManagerReturnToWorkCard() {
  const { language } = useLanguage();
  const isAr = language === 'ar';

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);   // row currently being confirmed
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/return-to-work/eligible`, { withCredentials: true });
      setItems(r?.data?.items || []);
    } catch (_e) {
      // Silent — the card just stays empty on transient failures.
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/return-to-work`,
        { employee_id: selected.employee_id, notes: notes.trim() },
        { withCredentials: true },
      );
      toast.success(isAr ? 'تم إرسال طلب توثيق المباشرة إلى الإدارة ✓' : 'Return-to-work submitted to admin ✓');
      // Optimistic remove
      setItems((prev) => prev.filter((r) => r.employee_id !== selected.employee_id));
      setSelected(null);
      setNotes('');
    } catch (e) {
      const msg = e?.response?.data?.detail || (isAr ? 'تعذّر الإرسال' : 'Submission failed');
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Hide entirely on empty state — reduces dashboard noise for managers
  // whose subordinates aren't returning today.
  if (loading || items.length === 0) return null;

  const fmtTime = (iso) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      return d.toLocaleTimeString(isAr ? 'ar-SA' : 'en-US', {
        hour: '2-digit', minute: '2-digit', hour12: true,
      });
    } catch { return ''; }
  };

  return (
    <>
      <Card className="border border-amber-200 bg-amber-50/40" data-testid="return-to-work-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <HandWaving size={22} weight="duotone" className="text-amber-600" />
              <h3 className="text-lg font-semibold text-[#655dc6]">
                {isAr ? 'مباشرة عمل بعد الإجازة' : 'Return to Work — Post Leave'}
              </h3>
              <Badge className="bg-amber-100 text-amber-800 border-amber-300">{items.length}</Badge>
            </div>
            <button
              onClick={load}
              className="text-[#655dc6]/70 hover:text-[#655dc6] p-1 rounded-md hover:bg-white transition-colors"
              title={isAr ? 'تحديث' : 'Refresh'}
              data-testid="rtw-refresh-btn"
            >
              <ArrowsClockwise size={16} weight="bold" />
            </button>
          </div>
          <p className="text-xs text-amber-800/80 mb-3 leading-relaxed">
            {isAr
              ? 'موظفون تحت إدارتك بصموا اليوم لأول مرة بعد إجازة سنوية أو غياب بدون عذر لا يقل عن 7 أيام عمل. اضغط "توثيق المباشرة" لإرسال طلب رسمي لمدير النظام.'
              : "Your direct reports who checked in today after an annual leave or unexcused absence of at least 7 working days. Click \"Confirm Return\" to file it with the admin."}
          </p>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-start text-[#655dc6]">
                    {isAr ? 'الموظف' : 'Employee'}
                  </TableHead>
                  <TableHead className="text-start text-[#655dc6]">
                    {isAr ? 'السبب' : 'Reason'}
                  </TableHead>
                  <TableHead className="text-start text-[#655dc6]">
                    {isAr ? 'المدة' : 'Duration'}
                  </TableHead>
                  <TableHead className="text-start text-[#655dc6]">
                    {isAr ? 'انتهاء الغياب' : 'Ended'}
                  </TableHead>
                  <TableHead className="text-start text-[#655dc6]">
                    {isAr ? 'أول بصمة اليوم' : "Today's 1st Punch"}
                  </TableHead>
                  <TableHead className="text-start text-[#655dc6]">
                    {isAr ? 'الإجراء' : 'Action'}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((it) => (
                  <TableRow key={it.employee_id} data-testid={`rtw-row-${it.employee_id}`}>
                    <TableCell className="font-medium text-[#655dc6]">
                      <div>{it.employee_name}</div>
                      {it.employee_name_en && (
                        <div className="text-[10px] text-[#655dc6]/60 leading-tight" dir="ltr">
                          {it.employee_name_en}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {(() => {
                        const cls = it.trigger === 'annual_leave'
                          ? 'bg-sky-100 text-sky-800 border-sky-200'
                          : it.trigger === 'sick_leave'
                          ? 'bg-amber-100 text-amber-800 border-amber-200'
                          : 'bg-rose-100 text-rose-800 border-rose-200';
                        const label = it.trigger === 'annual_leave'
                          ? (isAr ? 'إجازة سنوية' : 'Annual Leave')
                          : it.trigger === 'sick_leave'
                          ? (isAr ? 'إجازة مرضية' : 'Sick Leave')
                          : (isAr ? 'غياب بدون عذر' : 'Unexcused Absence');
                        return <Badge className={cls}>{label}</Badge>;
                      })()}
                    </TableCell>
                    <TableCell className="text-[#655dc6]/80 whitespace-nowrap">
                      {isAr
                        ? <>{it.working_days} <span className="text-[10px] text-[#655dc6]/60">يوم عمل</span></>
                        : <>{it.working_days} <span className="text-[10px] text-[#655dc6]/60">working days</span></>}
                    </TableCell>
                    <TableCell className="text-[#655dc6]/80">{it.last_leave_end}</TableCell>
                    <TableCell className="text-[#655dc6]/80">{fmtTime(it.first_punch_at)}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        onClick={() => { setSelected(it); setNotes(''); }}
                        className="bg-amber-600 hover:bg-amber-700 text-white h-8 gap-1"
                        data-testid={`rtw-confirm-btn-${it.employee_id}`}
                      >
                        <CalendarCheck size={16} weight="bold" />
                        {isAr ? 'توثيق المباشرة' : 'Confirm Return'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Confirmation dialog */}
      <Dialog open={!!selected} onOpenChange={(v) => { if (!v) setSelected(null); }}>
        <DialogContent dir={isAr ? 'rtl' : 'ltr'} className="sm:max-w-[440px]" data-testid="rtw-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#655dc6]">
              <CheckCircle size={22} weight="duotone" className="text-amber-600" />
              {isAr ? 'توثيق مباشرة عمل' : 'Confirm Return to Work'}
            </DialogTitle>
            <DialogDescription className="pt-2 text-sm text-[#655dc6]/80">
              {selected && (isAr
                ? <>
                    هل تُقرّ بمباشرة <strong>{selected.employee_name}</strong> للعمل اليوم بتاريخ <strong>{selected.return_date}</strong>
                    {' '}بعد {selected.trigger === 'annual_leave' ? 'إجازة سنوية' : selected.trigger === 'sick_leave' ? 'إجازة مرضية' : 'غياب بدون عذر'} استمر
                    {' '}<strong>{selected.working_days} يوم عمل</strong> (انتهى في {selected.last_leave_end})؟
                  </>
                : <>
                    Confirm that <strong>{selected.employee_name}</strong> resumed work today ({selected.return_date}) after {selected.trigger === 'annual_leave' ? 'an annual leave' : selected.trigger === 'sick_leave' ? 'a sick leave' : 'an unexcused absence'} of <strong>{selected.working_days} working days</strong> (ended {selected.last_leave_end})?
                  </>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <label className="text-xs text-[#655dc6]/70 block">
              {isAr ? 'ملاحظات (اختياري)' : 'Notes (optional)'}
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={isAr ? 'مثلاً: باشر مبكراً وأنهى ورديته كاملة…' : 'e.g. Reported early and completed full shift…'}
              rows={3}
              maxLength={400}
              data-testid="rtw-notes-input"
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={() => setSelected(null)}
              className="text-[#655dc6] hover:bg-[#EDE9FE]"
              data-testid="rtw-cancel-btn"
            >
              {isAr ? 'إلغاء' : 'Cancel'}
            </Button>
            <Button
              onClick={submit}
              disabled={submitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="rtw-submit-btn"
            >
              {submitting
                ? (isAr ? 'جارٍ الإرسال…' : 'Submitting…')
                : (isAr ? 'إرسال للاعتماد' : 'Submit for Approval')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
